import { Avatar, AvatarProps } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { OverlayPanel } from 'primereact/overlaypanel';
import { classNames } from 'primereact/utils';
import { MouseEvent, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import CompanyIcon from '../../../../../assets/icons/Company';
import { useAuth } from '../../../../../hooks/auth/useAuth';
import useDialogContext from '../../../../../hooks/dialog/useDialogContext';
import ReportBug from './report-bug/ReportBug';
import styles from './UserButton.module.css';

const UserButton = () => {
  const overlayPanelRef = useRef<OverlayPanel>(null);
  const { user, signOut, isCompanyAdmin, isBasfSuperAdmin } = useAuth();
  const { openDialog } = useDialogContext();

  const userInital = useMemo(() => {
    if (user?.givenName && user?.familyName) {
      return `${user.givenName?.charAt(0) ?? ''}${user.familyName?.charAt(0) ?? ''}`;
    }

    return null;
  }, [user]);

  const onButtonClick = (event: MouseEvent<HTMLAnchorElement>) => {
    overlayPanelRef.current?.toggle(event);
  };

  const onAboutClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    overlayPanelRef.current?.toggle(event);

    openDialog('about-dialog', {
      license: user?.company?.license,
    });
  };

  const onUserManagementClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    overlayPanelRef.current?.toggle(event);
    navigate('/user-management');
  };

  const onLegalNoticeClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    overlayPanelRef.current?.toggle(event);
    openDialog('legal-notice-dialog');
  };

  const renderAvatar = (props: AvatarProps) => {
    return userInital ? (
      <Avatar label={userInital} shape='circle' {...props} />
    ) : (
      <Avatar icon='pi pi-user' shape='circle' {...props} />
    );
  };

  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <a className={classNames('rounded-circle topbar-action')} onClick={onButtonClick}>
        <span className='icon-overlay'>
          {renderAvatar({
            className: classNames(styles['user-avatar']),
            pt: { label: { className: 'text-sm' } },
          })}
        </span>
      </a>

      <OverlayPanel
        ref={overlayPanelRef}
        className='shadow-2 mt-2'
        pt={{
          content: { className: 'p-0' },
        }}
      >
        <div className='flex flex-column'>
          <div className='flex m-4 gap-2'>
            {/* Avatar */}
            <div className='flex justify-content-center align-items-center'>
              {renderAvatar({
                className: classNames('rounded-circle', styles['user-avatar']),
                size: 'xlarge',
                pt: { label: { className: 'text-3xl font-semibold' } },
              })}
            </div>

            <div className='flex flex-column justify-content-between'>
              <div className='flex flex-column'>
                {/* Name */}
                {(user?.givenName || user?.familyName) && (
                  <span className='font-semibold text-black-alpha-90'>
                    {user?.givenName ?? ''} {user?.familyName ?? ''}
                  </span>
                )}

                {/* Email */}
                <span className='font-normal text-sm'>{user?.email}</span>
              </div>

              {/* Company */}
              <div className='flex align-items-center gap-1'>
                <CompanyIcon />
                <span className='text-sm'>{user?.company?.company_name}</span>
              </div>
            </div>
          </div>

          <Divider type='solid' className='p-0 m-0' />

          <ReportBug className={styles['list-action-button']} user={user} />

          {/* User Management Button */}
          {(isCompanyAdmin || isBasfSuperAdmin) && (
            <Button
              text
              className={classNames('pl-4 border-noround', styles['list-action-button'])}
              onClick={onUserManagementClick}
            >
              <i className='cb cb-icon-user-management user-icon' />
              <span className='pl-3'> {t('userButton.userManagement')}</span>
            </Button>
          )}

          {/* About Button */}
          <Button
            text
            className={classNames('pl-4 border-noround', styles['list-action-button'])}
            onClick={onAboutClick}
          >
            <i className='cb cb-icon-info user-icon' />
            <span className='pl-3'> {t('userButton.about')}</span>
          </Button>

          {/* Legal Notice Button */}
          <Button
            text
            className={classNames('pl-4 border-noround', styles['list-action-button'])}
            onClick={onLegalNoticeClick}
          >
            <i className='cb cb-icon-legal-notice user-icon' />
            <span className='pl-3'> {t('userButton.legalNotice')}</span>
          </Button>

          <Divider type='solid' className='p-0 m-0' />

          {/* Sign Out Button */}
          <Button
            className={classNames('pl-4 border-noround', styles['list-action-button'])}
            text
            onClick={signOut}
          >
            <i className='cb cb-icon-logout user-icon' />
            <span className='pl-3'> {t('userButton.signOut')}</span>
          </Button>
        </div>
      </OverlayPanel>
    </>
  );
};

export default UserButton;
